// 일반 카드결제의 네이티브 복귀 주소와 진행 중 주문을 한 곳에서 관리한다.
// 결제 승인 여부는 이 저장값이 아니라 서버의 PortOne 재조회 결과만 신뢰한다.

import { isNativeApp, NATIVE_DEEP_LINK_SCHEME } from "@/shared/platform.ts";

const PAYMENT_CALLBACK_HOST = "payment-complete";
const PENDING_DIRECT_PAYMENT_KEY = "oo-pending-direct-payment";
const PENDING_MAX_AGE_MS = 24 * 60 * 60 * 1_000;

// Android가 외부 결제 문서에서 앱 루트로 돌아올 때 원래 딥링크를 전달하는 일회성 쿼리 키다.
export const NATIVE_PAYMENT_RETURN_PARAM = "nativePaymentReturn";

interface PendingDirectPayment {
  orderId: string;
  startedAt: number;
  // 앱 활성화 시 주문 상세로 자동 복구한 시각. 한 번 표시한 화면으로 매 복귀마다
  // 강제 이동하지 않도록 하되, 서버 결제 확인에 필요한 pending 표식 자체는 유지한다.
  recoveryShownAt?: number;
}

export const NATIVE_PAYMENT_APP_SCHEME = `${NATIVE_DEEP_LINK_SCHEME}://`;

// 네이티브는 HTTPS localhost가 아니라 OS가 앱으로 전달할 커스텀 스킴을 완료 주소로 쓴다.
export function directPaymentRedirectUrl(orderId: string): string {
  if (isNativeApp) {
    return `${NATIVE_PAYMENT_APP_SCHEME}${PAYMENT_CALLBACK_HOST}?orderId=${encodeURIComponent(orderId)}`;
  }
  return `${window.location.origin}/me/orders/${encodeURIComponent(orderId)}`;
}

// 클라이언트 복귀가 끊겨도 PortOne이 서버 정합성 경로를 호출하도록 결제별 알림 주소를 지정한다.
export function directPaymentNoticeUrls(): string[] {
  const apiBase = String(import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/u, "");
  return apiBase ? [`${apiBase}/payments/portone-webhook`] : [];
}

// 앱 복귀가 끊겨도 다시 확인할 수 있도록 결제를 시작한 주문만 짧게 보관한다.
export function rememberPendingDirectPayment(orderId: string): void {
  const value: PendingDirectPayment = { orderId, startedAt: Date.now() };
  localStorage.setItem(PENDING_DIRECT_PAYMENT_KEY, JSON.stringify(value));
}

// 유효기간 안의 복구 대상 주문 ID를 읽고 손상·만료 데이터는 즉시 폐기한다.
export function pendingDirectPaymentOrderId(): string | null {
  const raw = localStorage.getItem(PENDING_DIRECT_PAYMENT_KEY);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<PendingDirectPayment>;
    if (
      typeof value.orderId !== "string" ||
      typeof value.startedAt !== "number" ||
      Date.now() - value.startedAt > PENDING_MAX_AGE_MS
    ) {
      clearPendingDirectPayment();
      return null;
    }
    return value.orderId;
  } catch {
    clearPendingDirectPayment();
    return null;
  }
}

// 앱 활성화/재실행 복구 화면은 결제 시도당 한 번만 연다.
// 반환된 주문은 서버 결제 확인 전까지 pending 상태를 유지하므로 주문 상세에서 계속 검증할 수 있다.
export function claimPendingDirectPaymentRecovery(): string | null {
  const raw = localStorage.getItem(PENDING_DIRECT_PAYMENT_KEY);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<PendingDirectPayment>;
    const orderId = pendingDirectPaymentOrderId();
    if (!orderId || value.recoveryShownAt) return null;
    localStorage.setItem(
      PENDING_DIRECT_PAYMENT_KEY,
      JSON.stringify({ ...value, orderId, recoveryShownAt: Date.now() }),
    );
    return orderId;
  } catch {
    clearPendingDirectPayment();
    return null;
  }
}

// 완료 딥링크가 먼저 도착한 경우에도 이후 appStateChange가 같은 화면을 다시 열지 않게 표시한다.
export function markPendingDirectPaymentRecoveryShown(orderId: string): void {
  const raw = localStorage.getItem(PENDING_DIRECT_PAYMENT_KEY);
  if (!raw) return;
  try {
    const value = JSON.parse(raw) as Partial<PendingDirectPayment>;
    if (value.orderId !== orderId || value.recoveryShownAt) return;
    localStorage.setItem(
      PENDING_DIRECT_PAYMENT_KEY,
      JSON.stringify({ ...value, recoveryShownAt: Date.now() }),
    );
  } catch {
    clearPendingDirectPayment();
  }
}

// 서버 결제완료 확인 또는 명시적 결제 실패 뒤 복구 표식을 제거한다.
export function clearPendingDirectPayment(orderId?: string): void {
  if (orderId && pendingDirectPaymentOrderId() !== orderId) return;
  localStorage.removeItem(PENDING_DIRECT_PAYMENT_KEY);
}
